/**
 * UX1A · Human-language presentation layer for USB fulfillment.
 *
 * Pure presentation mapping — mirrors backend/services/usb_ux_presentation.py
 * exactly. Internal state literals never change.
 */

export const STATUS_LABEL = {
  incomplete:   'Missing details',
  new:          'Ready to prepare',
  authorized:   'Preparing',
  building:     'Preparing USB',
  build_failed: 'Preparation needs attention',
  qc_pending:   'Ready for testing',
  qc_passed:    'Ready to ship',
  qc_failed:    'Failed integrity check',
  shipped:      'Shipped',
  closed:       'Complete',
  on_hold:      'Paused',
  rejected:     'Cancelled',
};

export const STATUS_TONE = {
  incomplete:   'warning',
  new:          'primary',
  authorized:   'info',
  building:     'info',
  build_failed: 'danger',
  qc_pending:   'info',
  qc_passed:    'success',
  qc_failed:    'danger',
  shipped:      'success',
  closed:       'neutral',
  on_hold:      'neutral',
  rejected:     'neutral',
};

export const TONE_STYLE = {
  primary: { bg: '#002FA7', fg: '#fff', border: '#002FA7' },
  success: { bg: '#DCFCE7', fg: '#166534', border: '#86EFAC' },
  info:    { bg: '#DBEAFE', fg: '#1E40AF', border: '#93C5FD' },
  warning: { bg: '#FEF3C7', fg: '#92400E', border: '#FDE68A' },
  danger:  { bg: '#FEE2E2', fg: '#991B1B', border: '#FCA5A5' },
  neutral: { bg: '#F3F4F6', fg: '#374151', border: '#D1D5DB' },
};

export const displayFulfillmentType = (order) => {
  const ft = order?.fulfillment_type;
  if (ft === 'demo') return 'Demo';
  if (ft === 'replacement') return 'Replacement';
  if (ft === 'internal') return 'Internal / Test';
  if (ft === 'customer') return 'Customer';
  return 'Legacy Customer';
};

/**
 * Pure derivation — mirrors backend derive_next_action() so the frontend
 * can render instantly without a round-trip. The backend endpoint is the
 * authoritative source of truth; this is just a client-side fallback.
 */
export const deriveNextAction = (order) => {
  const status = order?.status || 'new';
  const buyer = order?.buyer_contact || {};
  const ship  = order?.shipping_address || {};
  const missing = [];
  if (!buyer?.name) missing.push('buyer_contact.name');
  if (!ship?.line1) missing.push('shipping_address.line1');

  const mk = (action_id, label, subtitle, { destructive = false, disabled = false } = {}) => ({
    action_id, label, subtitle, destructive, disabled, needs: missing,
  });

  if (status === 'incomplete' || missing.length) {
    return mk('add_shipping',
      missing.includes('shipping_address.line1') ? 'Add shipping address' : 'Add details',
      'Missing details before this USB can be prepared');
  }
  if (status === 'new')          return mk('prepare_usb',       'Prepare USB',            'Ready to prepare');
  if (status === 'authorized' ||
      status === 'building')     return mk('preparing',          'Preparing USB…',         'This usually takes about 90 seconds.', { disabled: true });
  if (status === 'build_failed') return mk('needs_attention',    'Open support details',   'Preparation needs attention');
  if (status === 'qc_pending')   return mk('ready_for_testing',  'Open flashing checklist','Ready for testing (manual flashing until the MOSAIC USB Writer is released)');
  if (status === 'qc_failed')    return mk('try_again',          'Try again',              'This USB failed its integrity check — try again with a blank USB');
  if (status === 'qc_passed')    return mk('package_and_ship',   'Package & Ship',         'Ready to ship');
  if (status === 'shipped')      return mk('confirm_delivery',   'Confirm delivery',       `Shipped via ${order?.carrier || '—'} (${order?.tracking_number || 'tracking pending'})`);
  if (status === 'on_hold')      return mk('resume',             'Resume',                 'Paused · resume when ready');
  if (status === 'closed')       return mk('none', '', 'Complete',  { disabled: true });
  if (status === 'rejected')     return mk('none', '', 'Cancelled', { disabled: true });
  return mk('open_details', 'Open details', `Status: ${status}`);
};
